import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, Clock, MapPin } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  border: '1.5px solid var(--bw-border)',
  borderRadius: '10px',
  backgroundColor: 'var(--bw-white)',
  color: 'var(--bw-dark)',
  fontFamily: 'var(--font-body)',
  fontSize: '15px',
  outline: 'none',
  transition: 'border-color 0.15s',
};

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');
    try {
      const response = await fetch('/api/prospects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'contact_form', status: 'new' }),
      });
      if (!response.ok) throw new Error('Failed to submit form');
      setStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again or email us directly.');
      console.error('Form submission error:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Helmet>
        <title>Start a Conversation — BrightWings</title>
        <meta name="description" content="Tell us what's broken or unclear. We'll help you figure out what to build and whether it makes sense to work together." />
        <link rel="canonical" href="https://brightwings.io/contact" />
        <meta property="og:url" content="https://brightwings.io/contact" />
        <meta property="og:title" content="Start a Conversation — BrightWings" />
        <meta property="og:description" content="Tell us what's broken or unclear. We'll help you figure out what to build and whether it makes sense to work together." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Start a Conversation — BrightWings" />
        <meta name="twitter:description" content="Tell us what's broken or unclear. We'll help you figure out what to build and whether it makes sense to work together." />
      </Helmet>
      <Navigation />

      {/* Hero */}
      <section className="bw-hero">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bw-eyebrow-pill">Get in Touch</div>
            <h1 style={{ ...hStyle, fontSize: 'clamp(36px, 5.5vw, 68px)', marginBottom: '20px' }}>
              Let's <em style={{ fontStyle: 'normal', color: 'var(--bw-blue)' }}>talk.</em>
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '480px', margin: '0 auto' }}>
              Tell us what's broken or unclear. We'll help you figure out what to build and whether it makes sense to work together.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bw-section-surface">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 style={{ ...hStyle, fontSize: '28px', marginBottom: '32px' }}>Get in touch</h2>
              <div className="space-y-6">
                {[
                  { Icon: Mail, title: 'Email', body: <a href="mailto:hello@brightwings.io" style={{ color: 'var(--bw-blue)' }}>hello@brightwings.io</a> },
                  { Icon: Clock, title: 'Response time', body: <span style={{ color: 'var(--bw-text-mid)' }}>24–48 hours</span> },
                  { Icon: MapPin, title: 'Location', body: <span style={{ color: 'var(--bw-text-mid)' }}>Remote-first, Global</span> },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="bw-feature-icon flex-shrink-0" style={{ marginBottom: 0 }}>
                      <item.Icon size={18} color="var(--bw-blue)" />
                    </div>
                    <div>
                      <h3 style={{ ...hStyle, fontSize: '16px', marginBottom: '4px' }}>{item.title}</h3>
                      <div style={{ fontSize: '15px' }}>{item.body}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 pt-8" style={{ borderTop: '1px solid var(--bw-border)' }}>
                <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bw-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Trusted by founders
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bw-card">
              {status === 'success' ? (
                <div className="bw-card-body text-center py-12">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ backgroundColor: 'var(--bw-blue-light)' }}
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--bw-blue)' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 style={{ ...hStyle, fontSize: '22px', marginBottom: '12px' }}>Message received!</h3>
                  <p style={{ color: 'var(--bw-text-mid)', marginBottom: '32px', lineHeight: 1.6 }}>
                    Thank you for reaching out. We'll get back to you within 24–48 hours.
                  </p>
                  <button onClick={() => setStatus('idle')} className="bw-btn-secondary">
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bw-card-body space-y-5">
                  {[
                    { id: 'name', label: 'Name *', type: 'text', placeholder: 'John Doe' },
                    { id: 'email', label: 'Email *', type: 'email', placeholder: 'john@company.com' },
                    { id: 'company', label: 'Company *', type: 'text', placeholder: 'Acme Corp' },
                  ].map((field) => (
                    <div key={field.id}>
                      <label htmlFor={field.id} className="block text-sm font-medium mb-2" style={{ color: 'var(--bw-text-mid)' }}>
                        {field.label}
                      </label>
                      <input
                        type={field.type}
                        id={field.id}
                        name={field.id}
                        required
                        value={formData[field.id as keyof typeof formData]}
                        onChange={handleChange}
                        style={inputStyle}
                        placeholder={field.placeholder}
                        onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--bw-blue)'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--bw-border)'; }}
                      />
                    </div>
                  ))}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2" style={{ color: 'var(--bw-text-mid)' }}>
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      style={{ ...inputStyle, resize: 'none' }}
                      placeholder="Tell us about your project and how we can help..."
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--bw-blue)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--bw-border)'; }}
                    />
                  </div>

                  {status === 'error' && (
                    <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
                      <p className="text-sm" style={{ color: '#DC2626' }}>{errorMessage}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="bw-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    {status === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      'Send message'
                    )}
                  </button>
                  <p className="text-xs text-center" style={{ color: 'var(--bw-muted)' }}>
                    We'll only use this to get back to you.{' '}
                    <Link to="/privacy" onClick={() => window.scrollTo(0, 0)} style={{ color: 'var(--bw-blue)', textDecoration: 'underline' }}>
                      Privacy policy
                    </Link>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
